// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { maxLengthAttr } from "@flip/XDL/Utilities";
import { FormField, FormState } from "@flip/XDL/Utilities/form";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";

export interface AccountFormState extends FormState {
    fields: {
        isNewCustomer: FormField<string>;
        firstName: FormField<string>;
        lastName: FormField<string>;
        email: FormField<string>;
        phoneNumber: FormField<string>;
        website: FormField<string>;
        companyName: FormField<string>;
        businessType: FormField<string>;
        marketSpace: FormField<string>;
        title: FormField<string>;
        useIntity: FormField<string>;
        entityNameField: FormField<string>;
        mainContactFirstName: FormField<string>;
        mainContactLastName: FormField<string>;
        mainContactEmail: FormField<string>;
        mainContactPhoneNumber: FormField<string>;
        bAddress1: FormField<string>;
        bAddress2: FormField<string>;
        bCountry: FormField<string>;
        bState: FormField<string>;
        bCity: FormField<string>;
        bPostalCode: FormField<string>;
        financeContactName: FormField<string>;
        financePhoneNumber: FormField<string>;
        financeContactEmail: FormField<string>;
        useBillingAddressForShipping: FormField<boolean>;
        sAddress1: FormField<string>;
        sAddress2: FormField<string>;
        sCountry: FormField<string>;
        sState: FormField<string>;
        sCity: FormField<string>;
        sPostalCode: FormField<string>;
        sContactName: FormField<string>;
        sContactPhoneNumber: FormField<string>;
        sContactAccountNumber: FormField<string>;
    };
}

export const initialAccountFormState: AccountFormState = {
    isValid: false,
    isSubmitted: false,
    fields: {
        isNewCustomer: {
            value: "true",
            attrs: {
                id: "createAccount_isNewCustomer",
                name: "isNewCustomer",
            },
        },
        firstName: {
            value: "",
            attrs: {
                required: true,
                id: "createAccount_firstName",
                name: "firstName",
                label: translate("First Name"),
                maxLength: maxLengthAttr.firstName,
            },
        },
        lastName: {
            value: "",
            attrs: {
                required: true,
                id: "createAccount_lastName",
                name: "lastName",
                label: translate("Last Name"),
                maxLength: maxLengthAttr.lastName,
            },
        },
        email: {
            value: "",
            attrs: {
                required: true,
                type: "email",
                name: "email",
                id: "createAccount_email",
                label: translate("Email"),
            },
        },
        phoneNumber: {
            value: "",
            attrs: {
                required: true,
                id: "createAccount_phoneNumber",
                name: "phoneNumber",
                label: translate("Phone Number"),
                placeholder: "( ___ ) ___ - ____",
            },
        },
        companyName: {
            value: "",
            attrs: {
                required: true,
                id: "createAccount_companyName",
                name: "companyName",
                label: translate("Company Name"),
                maxLength: maxLengthAttr.companyName,
            },
        },
        website: {
            value: "",
            attrs: {
                required: true,
                id: "createAccount_website",
                name: "website",
                label: translate("Website"),
            },
        },
        businessType: {
            value: "",
            attrs: {
                required: true,
                id: "createAccount_businessType",
                name: "businessType",
                label: translate("Business Type"),
            },
        },
        marketSpace: {
            value: "",
            attrs: {
                required: true,
                id: "createAccount_marketSpace",
                name: "marketSpace",
                label: translate("Market Segment"),
            },
        },
        title: {
            value: "",
            attrs: {
                required: true,
                id: "createAccount_title",
                name: "title",
                label: translate("Title"),
                type: "text",
                maxLength: 100,
            },
        },
        useIntity: {
            value: "false",
            attrs: {
                id: "createAccount_useIntity",
                name: "useIntity",
            },
        },
        entityNameField: {
            value: "",
            attrs: {
                required: true,
                id: "createAccount_entityName",
                name: "entityName",
                label: translate("Entity Name"),
            },
        },
        mainContactFirstName: {
            value: "",
            attrs: {
                id: "createAccount_mainContactFirstName",
                name: "mainContactFirstName",
                label: translate("First Name"),
                maxLength: maxLengthAttr.firstName,
            },
        },
        mainContactLastName: {
            value: "",
            attrs: {
                id: "createAccount_mainContactLastName",
                name: "mainContactLastName",
                label: translate("Last Name"),
                maxLength: maxLengthAttr.lastName,
            },
        },
        mainContactEmail: {
            value: "",
            attrs: {
                id: "createAccount_mainContactEmail",
                name: "mainContactEmail",
                label: translate("Email"),
            },
        },
        mainContactPhoneNumber: {
            value: "",
            attrs: {
                id: "createAccount_mainContactPhoneNumber",
                name: "mainContactPhoneNumber",
                placeholder: "( ___ ) ___ - ____",
                label: translate("Phone Number"),
            },
        },
        bAddress1: {
            value: "",
            attrs: {
                required: true,
                id: "createAccount_bAddress1",
                name: "bAddress1",
                label: translate("Billing Address 1"),
            },
        },
        bAddress2: {
            value: "",
            attrs: {
                id: "createAccount_bAddress2",
                name: "bAddress2",
                label: translate("Billing Address 2"),
            },
        },
        bCountry: {
            value: "",
            attrs: {
                required: true,
                id: "createAccount_bCountry",
                name: "bCountry",
                label: translate("Country"),
            },
        },
        bState: {
            value: "",
            attrs: {
                required: true,
                id: "createAccount_bState",
                name: "bState",
                label: translate("State"),
            },
        },
        bCity: {
            value: "",
            attrs: {
                required: true,
                id: "createAccount_bCity",
                name: "bCity",
                label: translate("City"),
                maxLength: maxLengthAttr.city,
            },
        },
        bPostalCode: {
            value: "",
            attrs: {
                required: true,
                id: "createAccount_bPostalCode",
                name: "bPostalCode",
                label: translate("Postal/Zip Code"),
            },
        },
        financeContactName: {
            value: "",
            attrs: {
                required: true,
                id: "createAccount_financeContactName",
                name: "financeContactName",
                label: translate("Finance Contact Name"),
            },
        },
        financePhoneNumber: {
            value: "",
            attrs: {
                required: true,
                id: "createAccount_financePhoneNumber",
                name: "financePhoneNumber",
                placeholder: "( ___ ) ___ - ____",
                label: translate("Finance Contact Number"),
            },
        },
        financeContactEmail: {
            value: "",
            attrs: {
                required: true,
                id: "createAccount_financeContactEmail",
                name: "financeContactEmail",
                label: translate("Finance Contact Email"),
            },
        },
        useBillingAddressForShipping: {
            value: false,
            attrs: {
                id: "createAccount_useBillingAddressForShipping",
                name: "useBillingAddressForShipping",
            },
        },
        sAddress1: {
            value: "",
            attrs: {
                required: true,
                id: "createAccount_sAddress1",
                name: "sAddress1",
                label: translate("Shipping Address 1"),
            },
        },
        sAddress2: {
            value: "",
            attrs: {
                id: "createAccount_sAddress2",
                name: "sAddress2",
                label: translate("Shipping Address 2"),
            },
        },
        sCountry: {
            value: "",
            attrs: {
                required: true,
                id: "createAccount_sCountry",
                name: "sCountry",
                label: translate("Country"),
            },
        },
        sState: {
            value: "",
            attrs: {
                required: true,
                id: "createAccount_sState",
                name: "sState",
                label: translate("State"),
            },
        },
        sCity: {
            value: "",
            attrs: {
                required: true,
                id: "createAccount_sCity",
                name: "sCity",
                label: translate("City"),
                maxLength: maxLengthAttr.city,
            },
        },
        sPostalCode: {
            value: "",
            attrs: {
                required: true,
                id: "createAccount_sPostalCode",
                name: "sPostalCode",
                label: translate("Postal/Zip Code"),
            },
        },
        sContactName: {
            value: "",
            attrs: {
                required: true,
                id: "createAccount_shippingContactName",
                name: "shippingContactName",
                label: translate("Shipping Contact Name"),
            },
        },
        sContactPhoneNumber: {
            value: "",
            attrs: {
                required: true,
                id: "createAccount_shippingContactPhone",
                name: "shippingContactPhone",
                placeholder: "( ___ ) ___ - ____",
                label: translate("Shipping Contact Phone Number"),
            },
        },
        sContactAccountNumber: {
            value: "",
            attrs: {
                required: false,
                id: "createAccount_shippingAccountNum",
                name: "shippingAccountNum",
                label: translate("Ship Method and Account Number"),
            },
        },
    },
};
